import { useControllableValue, useDebounceFn, useRequest, useSetState } from 'ahooks';
import CustomSelect from '../Base/Select';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { SelectProps } from 'antd';

/**
 * 分页响应数据结构
 */
interface PaginationResponse<T> {
  records: T[];
  total?: number;
  pageIndex?: number;
  pageSize?: number;
}

/**
 * 分页请求参数基础结构
 * 不包含搜索字段，由 searchParamKey 动态控制
 */
interface PaginationParams {
  pageIndex: number;
  pageSize: number;
  [key: string]: any;
}

/**
 * 泛型分页选择器组件属性
 */
interface PaginationSelectorProps<T, P extends Record<string, any> = Record<string, any>> {
  /** 分页API调用函数 */
  fetchApi: (params: PaginationParams & P) => Promise<PaginationResponse<T>>;
  /** 额外的查询参数 */
  params?: P;
  /** 字段名映射配置 - 提供严格的类型约束 */
  fieldNames?: {
    label: keyof T;
    value: keyof T;
  };
  /** 输入框占位符 */
  placeholder?: string;
  /** 受控值 */
  value?: any;
  /** 值变化回调 */
  onChange?: (value: any, selectedData?: T) => void;
  /** 每页数据量，默认20 */
  pageSize?: number;
  /** 搜索防抖延迟，默认300ms */
  debounceWait?: number;
  /** 搜索参数字段名，默认 'search' */
  searchParamKey?: string;
  /** 是否支持多选 */
  multiple?: boolean;
  /** 选项数据转换函数，用于自定义选项的 label 和 value - 使用此选项时 fieldNames 将被忽略 */
  optionTransform?: (item: T) => { label: string; value: any; [key: string]: any };
  /** 默认选项列表，用于在第一页找不到数据时的回显，会与 API 返回的数据合并 */
  defaultOptions?: Array<{ label: string; value: any; [key: string]: any }>;
  style?: React.CSSProperties;
  disabled?: boolean;
  popupMatchSelectWidth?: SelectProps['popupMatchSelectWidth'];
  classNames?: SelectProps['classNames'];
  placement?: SelectProps['placement'];
  listHeight?: SelectProps['listHeight'];
  endReachedContent?: ReactNode;
}

/**
 * 通用分页选择器组件
 * 支持任意分页类型的数据接口下拉选择
 *
 * @example
 * ```tsx
 * // 示例1: 使用默认搜索字段 'search'
 * <PaginationSelector
 *   fetchApi={(params) => userApi.list(params)}
 *   fieldNames={{ label: 'userName', value: 'userId' }}
 *   placeholder="请选择用户"
 * />
 *
 * // 示例2: 使用自定义搜索字段 'keyword'
 * <PaginationSelector
 *   fetchApi={(params) => productApi.list(params)}
 *   searchParamKey="keyword"
 *   fieldNames={{ label: 'productName', value: 'id' }}
 * />
 *
 * // 示例3: 使用自定义搜索字段 'name'
 * <PaginationSelector
 *   fetchApi={(params) => companyApi.list(params)}
 *   searchParamKey="name"
 *   optionTransform={(item) => ({ label: item.companyName, value: item.id })}
 * />
 * ```
 */
const PaginationSelector = <T, P extends Record<string, any> = Record<string, any>>({
  fetchApi,
  params,
  fieldNames,
  placeholder = '请输入搜索',
  pageSize = 20,
  debounceWait = 300,
  searchParamKey = 'search',
  multiple = false,
  optionTransform,
  defaultOptions = [],
  style,
  disabled,
  popupMatchSelectWidth,
  classNames,
  placement,
  listHeight,
  endReachedContent,
  ...props
}: PaginationSelectorProps<T, P>) => {
  const [value, setValue] = useControllableValue({
    value: props.value,
    onChange: props.onChange,
  });

  const [searchParams, setSearchParams] = useSetState<PaginationParams & P>({
    pageIndex: 1,
    pageSize,
    ...params,
  } as PaginationParams & P);

  const [allRecords, setAllRecords] = useState<T[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);

  const getParamsBaseKey = (requestParams: PaginationParams & P) => {
    const { pageIndex: _pageIndex, ...rest } = requestParams;
    return JSON.stringify(rest);
  };
  const latestParamsBaseKeyRef = useRef(getParamsBaseKey(searchParams));

  const getOptionValue = (item: T) => {
    if (optionTransform) {
      return optionTransform(item).value;
    }
    if (fieldNames?.value) {
      return item[fieldNames.value];
    }
    return (item as any)?.value;
  };

  const { runAsync, loading } = useRequest(
    (requestParams: PaginationParams & P) => {
      return fetchApi(requestParams);
    },
    {
      manual: true,
      onSuccess: (res, [requestParams]) => {
        const currentBaseKey = latestParamsBaseKeyRef.current;
        const responseBaseKey = getParamsBaseKey(requestParams);
        if (responseBaseKey !== currentBaseKey) {
          return;
        }
        const nextRecords = res?.records || [];
        const responseTotal = Number(res?.total || 0);
        setReachedEnd(
          nextRecords.length < requestParams.pageSize ||
            (responseTotal > 0 && requestParams.pageIndex * requestParams.pageSize >= responseTotal)
        );
        setLoadingMore(false);
        setHasLoaded(true);
        setAllRecords((prev) => {
          const merged = requestParams.pageIndex === 1 ? nextRecords : [...prev, ...nextRecords];
          const valueSet = new Set<any>();
          return merged.filter((item) => {
            const value = getOptionValue(item);
            if (valueSet.has(value)) {
              return false;
            }
            valueSet.add(value);
            return true;
          });
        });
      },
    }
  );

  const updateList = useDebounceFn(
    (requestParams: PaginationParams & P) => {
      runAsync(requestParams);
    },
    { wait: debounceWait }
  );

  useEffect(() => {
    latestParamsBaseKeyRef.current = getParamsBaseKey(searchParams);
    if (searchParams.pageIndex === 1) {
      setHasLoaded(false);
      setReachedEnd(false);
    }
    if (disabled) {
      return;
    }
    updateList.run(searchParams);
  }, [searchParams, disabled]);

  const options = useMemo(() => {
    const apiOptions = optionTransform ? allRecords.map(optionTransform) : (allRecords as any[]);
    if (defaultOptions.length === 0) {
      return apiOptions;
    }
    const apiOptionValues = new Set((apiOptions || []).map((opt: any) => opt.value || opt[fieldNames?.value as any]));
    const missingDefaults = defaultOptions.filter((def) => !apiOptionValues.has(def.value));
    return [...missingDefaults, ...(apiOptions || [])];
  }, [allRecords, optionTransform, defaultOptions, fieldNames?.value]);

  const showEndReached = hasLoaded && !loading && !loadingMore && reachedEnd && allRecords.length > 0;

  return (
    <CustomSelect
      style={style}
      disabled={disabled}
      classNames={classNames}
      options={options}
      fieldNames={optionTransform ? undefined : (fieldNames as any)}
      value={value}
      onChange={(val) => {
        const selectedData = allRecords.find((item) => {
          const itemValue = optionTransform ? optionTransform(item).value : item[fieldNames?.value as keyof T];
          return itemValue === val || (multiple && Array.isArray(val) && val.includes(itemValue));
        });
        props.onChange?.(val, selectedData);
      }}
      placeholder={placeholder}
      mode={multiple ? 'multiple' : undefined}
      loading={loading}
      popupMatchSelectWidth={popupMatchSelectWidth}
      placement={placement}
      listHeight={listHeight}
      popupRender={
        endReachedContent
          ? (menu) => (
              <>
                {menu}
                {showEndReached && endReachedContent}
              </>
            )
          : undefined
      }
      showSearch={{
        filterOption: false,
        onSearch: (value: string) => {
          setSearchParams({ [searchParamKey]: value, pageIndex: 1 });
        },
      }}
      allowClear
      onClear={() => {
        setSearchParams({ [searchParamKey]: '', pageIndex: 1 });
      }}
      onPopupScroll={(event) => {
        if (loading || loadingMore || reachedEnd) {
          return;
        }
        const target = event.target as HTMLDivElement;
        if (!target) {
          return;
        }
        const reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 8;
        if (reachedBottom) {
          setLoadingMore(true);
          setSearchParams((prev) => ({
            ...prev,
            pageIndex: prev.pageIndex + 1,
          }));
        }
      }}
    />
  );
};

export default PaginationSelector;
